import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getConversation } from '../api/conversation';
import { ChatPlaceholder } from '../dashboard/ChatPlaceholder';
import { Message } from './Message';
import { ThinkingIndicator } from './ThinkingIndicator';
import { useSend } from './SendContext';
import { useT } from '../I18nContext';

/**
 * The chat surface - fetches the current conversation and renders
 * the turns. Auto-scrolls to the bottom on mount and whenever the
 * turn count grows (which only matters in D4+ when /ask appends;
 * harmless for D2's read-only path).
 *
 * Three states:
 *   - loading     small skeleton while /conversation resolves
 *   - empty       <ChatPlaceholder/> - same component the dashboard
 *                 shell rendered before D2
 *   - has-turns   stack of <Message/> bubbles
 *
 * No "infinite scroll for older messages" yet - the server returns
 * the entire active conversation in one shot (capped server-side
 * at 16 turns by chat_service to keep token usage sane). When we
 * add cross-conversation history (PR-D5 modal), that'll get its
 * own paginated endpoint.
 */
export function MessageStream() {
  const query = useQuery({
    queryKey: ['conversation'],
    queryFn: getConversation,
    // Refetch on focus - if the user opened the dashboard in a
    // second tab and chatted there, the original tab should
    // reflect the new state when they switch back.
    refetchOnWindowFocus: true,
    // Short stale time - conversations evolve. PR-D4's mutation
    // will invalidate this key directly so the stream updates
    // without waiting for a stale-time elapse.
    staleTime: 5_000,
  });

  const { isPending } = useSend();
  const t = useT();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastSeenCount = useRef(0);

  // Auto-scroll to bottom when:
  //   - first load completes
  //   - turn count grows (live append on send)
  //   - the thinking indicator appears (so the user sees it)
  useEffect(() => {
    const turns = query.data?.turns ?? [];
    const total = turns.length + (isPending ? 1 : 0);
    if (total === 0) return;
    if (total === lastSeenCount.current) return;
    lastSeenCount.current = total;
    // Defer to next paint so the new bubble has been laid out.
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    });
  }, [query.data?.turns?.length, isPending]);

  // ── Loading ──
  if (query.isLoading) {
    return (
      <div className="message-stream-loading">
        <p>{t('conversation.loading')}</p>
      </div>
    );
  }

  // ── Error (non-401; 401 already bounced via MeProvider) ──
  if (query.isError) {
    return (
      <div className="message-stream-error">
        <p>{t('conversation.load_failed')}</p>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>
          Refresh the page; if it keeps happening, contact your administrator.
        </p>
      </div>
    );
  }

  const turns = query.data?.turns ?? [];

  // ── Empty (and not currently sending the first message) ──
  if (turns.length === 0 && !isPending) {
    return <ChatPlaceholder />;
  }

  // ── Has turns ──
  return (
    <div className="message-stream" ref={scrollRef}>
      {turns.map((turn, i) => (
        // Stable key from timestamp + role + index - the legacy
        // history doesn't carry per-turn ids, and turns can repeat
        // (rare, but the rebuild on /reset wipes them anyway).
        <Message
          key={`${turn.timestamp ?? 'ts'}-${turn.role}-${i}`}
          turn={turn}
        />
      ))}
      {isPending && <ThinkingIndicator />}
    </div>
  );
}
